import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { FocusTrapFactory } from '@angular/cdk/a11y';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  AccessibleOverlayHandle,
  makeOverlayAccessible
} from '../overlay/accessible-overlay';
import {
  ComponentRef,
  computed,
  Injectable,
  Injector,
  signal,
  inject
} from '@angular/core';
import { debounceTime, firstValueFrom, noop, Subscription } from 'rxjs';
import { UserProfileFacade } from '@cpd-app/state/user-profile';
// Type-only import — erased at runtime so it does NOT pull the overlay
// component into the eager bundle. The actual class is fetched via dynamic
// import on first openJstSidebar() call.
import type { JstUploadSidebarOverlayComponent } from '@cpd-app/core-module/components/jst-upload-sidebar/jst-upload-sidebar-overlay.component';
import {
  CourseRow,
  JstApiService,
  JstDryRunResponse,
  JstLatestResponse,
  JstUploadListItem,
  OccupationRow,
  TestRow
} from './jst-api.service';
import { AceApiService } from '@cpd-app/core-module/services/ace/ace-api.service';
// A plain constants module, not the component: safe in the eager bundle.
import { TRANSCRIPT_READ_V3 } from '@cpd-app/core-module/components/jst-upload-sidebar/transcript-read-progress/transcript-read-progress';

// How long the panel stays open after a parse starts before sliding closed.
// Long enough to read as a response to the learner's action, short enough that
// nobody settles in to watch the spinner. Only used when TRANSCRIPT_READ_V3
// is rolled back; v3 keeps the panel open for the labelled bar.
const PARSING_AUTO_CLOSE_MS = 600;

/**
 * States the panel can be re-entered from, i.e. where nothing is in flight.
 *
 * The landing decision below only runs for these. It used to test
 * `=== 'upload'`, which had a bug: uploadViewState lives on this root-provided
 * singleton and so survives the panel closing, meaning that after a learner
 * confirmed an upload the state stayed 'saved' forever. Every later reopen
 * failed the check, loadDocuments() never ran, and they were returned to their
 * saved transcript with no way to reach the documents list. It only ever worked
 * on a fresh page load.
 *
 * 'saved' and 'documents' are resting states -- the learner is looking at
 * something finished -- so reopening from them should re-read the list.
 * 'parsing', 'saving', 'confirm' and the error states are deliberately excluded:
 * those ARE in flight, and clobbering them would drop a learner out of a live
 * parse or an unconfirmed review.
 */
const RESTING_VIEW_STATES: ReadonlySet<ViewState> = new Set<ViewState>([
  'chooser',
  'upload',
  'saved',
  'documents'
]);

/**
 * The size ceiling, and the reason it is checked here rather than server-side.
 *
 * 03 section 5 wants the failures that are knowable before the read to cost
 * nobody a wait: the file's own size is one of them, so it is checked the
 * moment a file is picked rather than after a 30 to 45 second parse.
 */
export const MAX_TRANSCRIPT_BYTES = 10 * 1024 * 1024;

// Upload flow state machine. Owned by the service (not the overlay) so the
// state survives panel close/reopen; the overlay reads via computed signals.
export type ViewState =
  // The learner's uploaded documents. Where the panel lands when they already
  // have at least one, instead of dropping them onto the newest upload's
  // success screen with no way to see the others.
  | 'documents'
  // Which kind of transcript. This is the panel's first screen for a learner
  // with nothing on file, and it exists to name what we cannot read YET:
  // CCAF is listed as coming soon rather than hidden, so an Air Force member
  // finds out before they spend a wait on it rather than after. The choice
  // also decides what gets asked next, since a JST needs a branch and an ACE
  // transcript does not.
  | 'chooser'
  | 'upload'
  | 'parsing'
  | 'confirm'
  | 'saving'
  | 'saved'
  // Kept as the server-side backstop. The chooser is what should prevent an
  // Air Force member reaching it, but a CCAF can still arrive as a JST.
  | 'error-airforce'
  // The two failures that are knowable without a read, so neither costs a wait.
  | 'error-size'
  | 'error-limit'
  | 'error-parse';

/** Which kind of transcript the learner said they have. */
export type TranscriptKind = 'jst' | 'ace';

/**
 * One row of the documents list: the learner's JSTs and ACE transcripts in a
 * single chronological list.
 *
 * `kind` is the discriminant a row's click handler branches on. `id` is only
 * unique WITHIN a kind (a jst_upload_id and an ace_upload_id can collide), so
 * never key a template loop on `id` alone.
 */
export interface DocumentListItem {
  kind: 'jst' | 'ace';
  id: number;
  uploadedAt: string;
  /**
   * JST only. A JST re-upload supersedes its predecessor, so an inactive row no
   * longer counts toward credit — which is the whole reason it is shown rather
   * than hidden. ACE transcripts accumulate instead, so they are always active.
   */
  isActive: boolean;
  /** Pre-formatted counts, e.g. "21 courses · 5 occupations · 11 tests". */
  summary: string;
}

// Narrowed type for the saved-result cache: only success cases are stored.
// A success:false response from the API is treated the same as "no fetch yet"
// for rendering purposes -- both leave savedJstResult() === null.
type SavedJstResult = Extract<JstLatestResponse, { success: true }>;

/**
 * Counts line for a JST row. Omits groups with nothing in them rather than
 * printing "0 tests", and falls back to a plain label when an upload parsed
 * to nothing at all -- a row reading "0 courses · 0 occupations · 0 tests" looks
 * like a bug, whereas "No records found" reads as the fact it is.
 */
function describeJstCounts(upload: JstUploadListItem): string {
  const parts: string[] = [];
  if (upload.courseCount > 0) {
    parts.push(
      `${upload.courseCount} ${upload.courseCount === 1 ? 'course' : 'courses'}`
    );
  }
  if (upload.occupationCount > 0) {
    parts.push(
      `${upload.occupationCount} ${upload.occupationCount === 1 ? 'occupation' : 'occupations'}`
    );
  }
  if (upload.testCount > 0) {
    parts.push(
      `${upload.testCount} ${upload.testCount === 1 ? 'test' : 'tests'}`
    );
  }
  return parts.length > 0 ? parts.join(' · ') : 'No records found';
}

@Injectable({
  providedIn: 'root'
})
export class JstSidebarService {
  private readonly isSidebarOpen$ = signal<boolean>(false);
  private overlayRef!: OverlayRef;
  // Accessibility behavior for the raw CDK overlay; released on detach.
  private a11y: AccessibleOverlayHandle | null = null;
  private portal: ComponentPortal<JstUploadSidebarOverlayComponent> | null =
    null;
  private componentRef: ComponentRef<JstUploadSidebarOverlayComponent> | null =
    null;
  // The CDK ComponentPortal is reused across opens (we only construct it
  // once for lazy-loading reasons). EventEmitter's subscriber list isn't
  // cleared on overlayRef.detach(), so without explicit teardown each
  // re-open would stack a new closedSidebar subscriber and the close
  // handler would fire N times after N open/close cycles.
  private closedSidebarSubscription: Subscription | null = null;

  public readonly isSidebarOpen = computed(() => this.isSidebarOpen$());

  // Session-level cache of the user's most recent confirmed JST upload.
  // Set once on first open if the profile says they've uploaded; survives
  // overlay detach/reattach so reopening shows the saved state without
  // refetching.
  public readonly savedJstResult = signal<SavedJstResult | null>(null);
  public readonly hasFetchedSavedJst = signal<boolean>(false);

  // Latched by resetUploadFlow() ("Upload a new JST") and held until the
  // replacement is confirmed or explicitly abandoned.
  //
  // Without this latch the button is a dead end: resetUploadFlow() rewound the
  // FSM to 'upload' but also re-armed the hydrate path, so the next
  // openJstSidebar() re-fetched the user's EXISTING JST and slammed them
  // straight back to the 'saved' view. A user could only replace a JST in one
  // uninterrupted sitting -- closing the panel, or any re-open, threw the
  // attempt away.
  private readonly isReplacingJst$ = signal<boolean>(false);
  public readonly isReplacingJst = computed(() => this.isReplacingJst$());

  // Upload-flow state. Lives on the service so an in-flight parse + its
  // result survive panel close/reopen -- the user can dismiss the panel
  // mid-parse and we'll auto-reopen them on the review screen when the
  // dryRun completes.
  public readonly uploadViewState = signal<ViewState>('chooser');

  /**
   * Which transcript the learner picked in the chooser. A JST needs a branch
   * and an ACE transcript has none, so the upload screen reads this rather
   * than asking everyone for a branch.
   */
  public readonly transcriptKind = signal<TranscriptKind>('jst');

  /**
   * How many uploads are left today. Shown on the upload screen before a file
   * is picked, so the ceiling is a fact the learner has rather than a refusal
   * they run into. `null` while unknown, which renders nothing at all.
   */
  public readonly uploadsRemainingToday = signal<number | null>(null);
  public readonly uploadErrorMessage = signal<string | null>(null);

  /**
   * A machine-readable code for a parse failure, when the reader gives one.
   * Rendered only when present: a member reporting "it would not read my
   * transcript" has something to quote, and nobody sees an empty code line.
   */
  public readonly uploadErrorCode = signal<string | null>(null);
  public readonly uploadParsedResult = signal<JstDryRunResponse | null>(null);

  /**
   * When the current read started, so the wait can be drawn against the
   * duration it actually takes.
   *
   * 03 section 5: "This takes 30-45 seconds. Design for the wait; do not show
   * an indeterminate spinner and hope." A spinner says only "something is
   * happening"; an elapsed bar against a known median says roughly how much
   * longer. It deliberately never reaches full on time alone, because arriving
   * at 100% while the reader is still working would be a claim we cannot make.
   */
  public readonly parseStartedAt = signal<number | null>(null);
  /**
   * The name of the file being read, so a surface that did not pick it (the
   * Skills Survey, or the panel after a close and reopen) can still name it.
   */
  public readonly parsingFileName = signal<string | null>(null);

  /**
   * Walkthrough 09-29 #13 v3: the panel stays open for the whole read, on the
   * one-column reading view with a labelled bar, and the Skills Survey shows
   * the same read as a card. False is the rollback (TRANSCRIPT_READ_V3): the
   * panel closes itself 600ms in and the old views come back. Read by the
   * panel and the survey so the three always agree; writable for the specs.
   */
  public readV3 = TRANSCRIPT_READ_V3;
  public readonly uploadEditableOccupations = signal<OccupationRow[]>([]);
  public readonly uploadEditableCourses = signal<CourseRow[]>([]);
  public readonly uploadEditableTests = signal<TestRow[]>([]);

  // True while a transcript is being read. Exposed so surfaces OUTSIDE the
  // panel can show it -- the sidebar's upload icon carries a ring, which is
  // the only progress signal left once the panel closes itself.
  public readonly isParsing = computed(
    () => this.uploadViewState() === 'parsing'
  );

  /**
   * CPD-670. The member chose to answer by hand rather than watch THIS parse.
   *
   * It was a signal private to the Skills Survey, so the panel's own "Skip and
   * answer the questions myself" could only close the panel -- exactly what
   * its X does -- and the survey behind it went on showing the wait. Held here
   * so either Skip can set it and the survey reads the same answer.
   *
   * A decision about one parse, not a preference: `startUpload` clears it, so
   * a second transcript gets its own progress.
   */
  public readonly waitSkipped = signal(false);

  // The documents list. Refetched on each open rather than cached for the
  // session: a confirm, or an upload in the ACE panel, changes it, and a stale
  // list that silently omits the document the learner just added is worse than
  // one extra pair of cheap GETs.
  public readonly documents = signal<DocumentListItem[]>([]);

  // Whether `documents` was loaded during the CURRENT open. Cleared on close, so
  // every fresh open still refetches. Its only job is to let one click ask the
  // list two questions -- resolveUploadEntry() below needs to know whether the
  // learner has anything, and openJstSidebar() then needs the rows themselves --
  // without paying for two pairs of GETs.
  private documentsFresh = false;

  constructor(
    private readonly overlay: Overlay,
    private readonly injector: Injector,
    private readonly jstApiService: JstApiService,
    private readonly aceApiService: AceApiService,
    private readonly userProfileFacade: UserProfileFacade,
    // Kept last on purpose: the specs construct this service positionally, so
    // inserting anywhere else silently shifts every existing argument.
    private readonly focusTrapFactory: FocusTrapFactory
  ) {}

  /**
   * Build the documents list from both upload types.
   *
   * Best-effort on each half independently. ACE transcripts sit behind their own
   * feature flag, so that call can legitimately fail while JST is fine — and a
   * learner with three JSTs should still see three rows rather than an error.
   * Promise.allSettled, not Promise.all, for exactly that reason: `all` would
   * let the disabled half blank the whole list.
   */
  public async loadDocuments(): Promise<void> {
    const [jst, ace] = await Promise.allSettled([
      firstValueFrom(this.jstApiService.listUploads()),
      firstValueFrom(this.aceApiService.listTranscripts())
    ]);

    const rows: DocumentListItem[] = [];

    if (jst.status === 'fulfilled') {
      for (const upload of jst.value ?? []) {
        rows.push({
          kind: 'jst',
          id: upload.jstUploadId,
          uploadedAt: upload.uploadedAt,
          isActive: upload.isActive,
          summary: describeJstCounts(upload)
        });
      }
    }

    if (ace.status === 'fulfilled') {
      for (const transcript of ace.value?.transcripts ?? []) {
        const count = transcript.data?.items?.length ?? 0;
        rows.push({
          kind: 'ace',
          id: transcript.ace_upload_id,
          uploadedAt: transcript.uploaded_at,
          // ACE transcripts accumulate rather than superseding one another.
          isActive: true,
          summary: `${count} ${count === 1 ? 'credential' : 'credentials'}`
        });
      }
    }

    rows.sort(
      (a, b) =>
        new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime()
    );
    this.documents.set(rows);
    this.documentsFresh = true;
    /*
     * CPD-656. Whether the list above is the whole truth.
     *
     * `allSettled` means a rejected source contributes no rows and says
     * nothing, so a learner WITH documents and a failed fetch is
     * indistinguishable here from a learner with none. That difference
     * decides which screen the Upload button opens, and getting it wrong
     * sends somebody who has transcripts to the "which kind do you have?"
     * chooser, with no route back to the ones they already uploaded.
     */
    this.documentsComplete =
      jst.status === 'fulfilled' && ace.status === 'fulfilled';
  }

  /**
   * False when the last `loadDocuments` could not reach one of its sources.
   * An empty list only means "this learner has nothing" when this is true.
   */
  private documentsComplete = false;

  /**
   * Which screen the sidebar's Upload button should open.
   *
   * 'jst-panel' -> the JST panel, whose own landing logic shows the document
   * list when the learner has anything stored. That is the right destination for
   * a returning learner: it shows what they have already uploaded, and its "Add
   * another document" button is what opens the doc-type picker. Sending them
   * straight to the picker instead makes the document list unreachable from the
   * one control that exists to reach it.
   *
   * 'picker' -> the JST-vs-ACE doc-type picker, for a learner with nothing
   * stored. A document list is not worth showing when it would be empty.
   *
   * `isAceEnabled` is passed in rather than read here so this rule stays unit
   * testable and this service stays free of a dependency on the ACE panel. With
   * the flag off there is no picker to open, so the answer is always the JST
   * panel -- which handles the empty case by landing on its upload screen.
   */
  public async resolveUploadEntry(
    isAceEnabled: boolean
  ): Promise<'jst-panel' | 'picker'> {
    if (!isAceEnabled) {
      return 'jst-panel';
    }

    /*
     * CPD-656. A parse in flight owns the panel, whatever the document count
     * says.
     *
     * `startUpload` closes the panel 600ms in and the parse runs on without
     * it, so reopening is the only way back to the "reading your transcript"
     * screen. But the count below only counts CONFIRMED uploads, and on a
     * learner's FIRST transcript that is still zero while their parse is
     * running -- so this answered 'picker', the doc-type chooser opened, and
     * the live parse was nowhere on screen. On every later upload the count
     * is already one or more, the JST panel opens, and 'parsing' is shown
     * correctly. That is the whole of "it works the second time".
     */
    if (this.uploadViewState() === 'parsing') {
      return 'jst-panel';
    }

    if (!this.documentsFresh) {
      await this.loadDocuments();
    }

    if (this.documents().length > 0) {
      return 'jst-panel';
    }

    /*
     * CPD-656. An empty list we could not trust is not an empty learner.
     *
     * A failed fetch used to read as "nothing stored" and open the doc-type
     * chooser, which is the ACE panel's type-select -- so a learner with
     * transcripts landed on "which kind do you have?" and had no way back to
     * the documents they already had. This is the one code path that
     * produces that symptom without anybody clicking an ACE row, and it is
     * intermittent by nature, which fits QA's "sometimes".
     *
     * The JST panel is the safe answer when we are unsure: its own landing
     * logic shows the list when there is one and its upload screen when
     * there is not, so it is right either way.
     */
    return this.documentsComplete ? 'picker' : 'jst-panel';
  }

  /**
   * Open one JST row from the documents list.
   *
   * Reuses the saved-result cache when the row IS the active upload, which is
   * the common case and avoids a refetch. Anything else -- notably a superseded
   * upload -- is fetched by id.
   */
  public async openJstDocument(jstUploadId: number): Promise<void> {
    const cached = this.savedJstResult();
    if (cached?.jst_upload_id === jstUploadId) {
      this.applySavedJstResultToUploadState();
      this.uploadViewState.set('saved');
      return;
    }

    const result = await firstValueFrom(
      this.jstApiService.getUploadById(jstUploadId)
    ).catch(() => ({ success: false }) as const);

    if (!result.success) {
      // Leave the learner on the list rather than on an empty detail screen.
      return;
    }

    this.uploadParsedResult.set({
      success: true,
      message: 'Loaded from a previous upload',
      jst_upload_id: result.jst_upload_id,
      data: result.data
    });
    this.uploadEditableOccupations.set(result.data.militaryOccupations ?? []);
    this.uploadEditableCourses.set(result.data.militaryCourses ?? []);
    this.uploadEditableTests.set(result.data.collegeLevelTests ?? []);
    this.uploadViewState.set('saved');
  }

  /** Back out of a document's detail to the list. */
  public backToDocuments(): void {
    this.uploadViewState.set('documents');
  }

  public async openJstSidebar(): Promise<void> {
    if (!this.portal) {
      // Lazy-load the overlay component. Keeping it out of the eager bundle
      // means JstSidebarService can be safely imported by sidebar.component
      // at app boot without dragging the overlay's transitive deps into
      // the early load chain (which intersects SharedModule's load path).
      const componentModule = await import(
        '@cpd-app/core-module/components/jst-upload-sidebar/jst-upload-sidebar-overlay.component'
      );
      this.portal = new ComponentPortal(
        componentModule.JstUploadSidebarOverlayComponent,
        null,
        this.injector
      );
    }

    if (!this.overlayRef) {
      this.createOverlay();
    }

    if (this.overlayRef.hasAttached()) {
      this.closeJstSidebar();
      return;
    }

    // Hydrate the saved-result cache BEFORE attach so the overlay's first
    // render reads the right view state from the service signals (avoiding a
    // brief flash of the upload screen on cross-session reopen). Skipped when
    // an upload flow is already in progress so an auto-reopen lands on the
    // active confirm/error view, not the prior-saved state -- and skipped
    // while the user is deliberately replacing their JST, so re-opening the
    // panel mid-replacement does not drag them back to the saved view.
    if (
      RESTING_VIEW_STATES.has(this.uploadViewState()) &&
      !this.isReplacingJst()
    ) {
      await this.hydrateSavedJstResult();
      this.applySavedJstResultToUploadState();
      if (!this.documentsFresh) {
        await this.loadDocuments();
      }
      // Land on the documents list whenever the learner has anything, so they
      // can see and revisit every upload instead of only the newest.
      //
      // Gated on the LIST, not on savedJstResult: a learner with ACE
      // transcripts but no JST has documents to show while savedJstResult is
      // null, and gating on the latter would strand them on the upload screen.
      // With nothing at all, the upload screen remains correct.
      if (this.documents().length > 0) {
        this.uploadViewState.set('documents');
      }
    }

    this.isSidebarOpen$.set(true);
    this.componentRef = this.overlayRef.attach(this.portal);
    this.a11y = makeOverlayAccessible(this.overlayRef, this.focusTrapFactory, {
      ariaLabel: 'Upload your Joint Services Transcript',
      onEscape: () => this.closeJstSidebar()
    });
    // Tear down any subscription left over from a prior open before
    // creating the new one. Without this, the EventEmitter accumulates
    // subscribers across open/close cycles.
    this.closedSidebarSubscription?.unsubscribe();
    this.closedSidebarSubscription = this.componentRef.instance.closedSidebar
      .pipe(debounceTime(500))
      .subscribe({
        next: () => this.closeJstSidebar(),
        error: noop
      });
  }

  // Mirror the savedJstResult cache onto the upload-flow signals so the
  // overlay's reactive reads pick up the saved view on attach. No-op when
  // there's no cached result.
  private applySavedJstResultToUploadState(): void {
    const saved = this.savedJstResult();
    if (!saved) {
      return;
    }
    this.uploadParsedResult.set({
      success: true,
      message: 'Loaded from previous upload',
      jst_upload_id: saved.jst_upload_id,
      data: saved.data
    });
    this.uploadEditableOccupations.set(saved.data.militaryOccupations ?? []);
    this.uploadEditableCourses.set(saved.data.militaryCourses ?? []);
    this.uploadEditableTests.set(saved.data.collegeLevelTests ?? []);
    this.uploadViewState.set('saved');
  }

  // Reset every upload-flow signal back to its initial value and latch
  // "replacing". Called by the overlay's "Upload a new JST" button.
  //
  // Deliberately KEEPS savedJstResult: the user has not replaced anything yet,
  // so their existing record must survive for cancelReplaceJst() to restore.
  // It is overwritten on a successful confirm via setSavedJstResult().
  public resetUploadFlow(): void {
    this.uploadViewState.set('upload');
    this.uploadErrorMessage.set(null);
    this.uploadErrorCode.set(null);
    this.uploadParsedResult.set(null);
    this.uploadEditableOccupations.set([]);
    this.uploadEditableCourses.set([]);
    this.uploadEditableTests.set([]);
    this.isReplacingJst$.set(true);
  }

  // "Never mind, keep what I have." Drops the replacement attempt and puts the
  // user back on their existing saved JST. Falls back to the upload screen if
  // there is nothing cached to go back to.
  public cancelReplaceJst(): void {
    this.isReplacingJst$.set(false);
    if (this.savedJstResult()) {
      this.applySavedJstResultToUploadState();
    } else {
      this.uploadViewState.set('upload');
    }
  }

  public clearSavedJstResult(): void {
    this.savedJstResult.set(null);
    this.hasFetchedSavedJst.set(false);
  }

  // Kicks off the dryRun upload. State updates land on service signals so
  // the parse survives a panel close; the overlay re-binds via computed
  // reads when it re-attaches.
  public startUpload(file: File): void {
    this.waitSkipped.set(false);
    this.uploadViewState.set('parsing');
    this.parseStartedAt.set(Date.now());
    this.parsingFileName.set(file.name);
    this.uploadErrorMessage.set(null);
    this.uploadErrorCode.set(null);
    // v3 keeps the panel open: the labelled bar is worth watching, and the
    // member can still close it, in which case reopenIfDetached() below
    // brings them back when the read lands, as before.
    if (!this.readV3) {
      this.scheduleParsingAutoClose();
    }
    this.jstApiService.uploadDryRun(file).subscribe({
      next: (response) => {
        this.uploadParsedResult.set(response);
        if (this.looksLikeAirForce(response.data?.personalInfo?.branch)) {
          this.uploadViewState.set('error-airforce');
        } else {
          this.uploadEditableOccupations.set(
            response.data.militaryOccupations ?? []
          );
          this.uploadEditableCourses.set(response.data.militaryCourses ?? []);
          this.uploadEditableTests.set(response.data.collegeLevelTests ?? []);
          this.uploadViewState.set('confirm');
        }
        this.reopenIfDetached();
      },
      error: (err) => {
        // The server's words when it sent any (a 4xx refusal), otherwise
        // nothing: the panel's plain fallback reads better than the
        // interceptor's "HTTP error with status code: 500" (QA.20).
        const message = err?.error?.message ?? null;
        this.uploadErrorMessage.set(message);
        // QA.19 U-7: a refused file (JST_ENCRYPTED, JST_NOT_A_TRANSCRIPT)
        // carries a code; the panel shows its own words for each.
        // QA.20: a 429 is the daily or rolling upload limit, not the file.
        this.uploadErrorCode.set(
          err?.error?.code ?? (err?.status === 429 ? 'JST_UPLOAD_LIMIT' : null)
        );
        this.uploadViewState.set('error-parse');
        this.reopenIfDetached();
      }
    });
  }

  /**
   * Slide the panel closed shortly after a parse starts.
   *
   * A transcript takes roughly 50 seconds to read, and holding someone still
   * watching a spinner for that long is the worst use of their time -- the
   * upload state already lives on this service precisely so the parse survives
   * the panel going away, and reopenIfDetached() brings them back when it
   * finishes.
   *
   * The delay is deliberate: closing instantly reads as the click having failed,
   * whereas a beat of the parsing view first reads as a response to their
   * action.
   */
  private scheduleParsingAutoClose(): void {
    setTimeout(() => {
      // Re-check rather than assume. A cached (byte-identical) upload can
      // resolve in well under this delay, in which case the state machine has
      // already moved to confirm and reopened the panel -- closing it here
      // would throw the learner out of the screen they were waiting for.
      if (this.uploadViewState() !== 'parsing') {
        return;
      }
      if (!this.overlayRef?.hasAttached()) {
        return;
      }
      this.closeJstSidebar();
    }, PARSING_AUTO_CLOSE_MS);
  }

  private reopenIfDetached(): void {
    // The user may have closed the panel while the dryRun was in flight.
    // Re-attach so they land on whatever view the upload state machine
    // transitioned to (confirm / error-parse / error-airforce).
    if (this.overlayRef && !this.overlayRef.hasAttached()) {
      this.openJstSidebar();
    }
  }

  private looksLikeAirForce(branch: string | null | undefined): boolean {
    if (!branch) {
      return false;
    }
    return /air\s*force|usaf|ccaf/i.test(branch);
  }

  // Called by the overlay after a successful confirm. Writes the just-saved
  // payload into the cache so the next sidebar reopen skips the fetch path
  // and renders the saved view directly. Without this, an in-session
  // upload -> save -> close -> reopen sequence falls back to the upload
  // screen because hydrateSavedJstResult's "already fetched" guard short-
  // circuits before the (now-stale) isJstUploaded=false flag updates.
  public setSavedJstResult(result: SavedJstResult): void {
    this.savedJstResult.set(result);
    this.hasFetchedSavedJst.set(true);
    // The replacement landed, so the latch is done: subsequent opens should
    // hydrate normally against the new record.
    this.isReplacingJst$.set(false);
  }

  private async hydrateSavedJstResult(): Promise<void> {
    if (this.hasFetchedSavedJst()) {
      return;
    }

    const isUploaded = await firstValueFrom(
      this.userProfileFacade.isJstUploaded$
    );
    if (!isUploaded) {
      this.hasFetchedSavedJst.set(true);
      return;
    }

    try {
      const result = await firstValueFrom(this.jstApiService.getLatestJst());
      if (result.success) {
        this.savedJstResult.set(result);
      }
    } catch {
      // Swallow -- the overlay falls back to its default upload screen if we
      // can't fetch saved data. No user-visible error: they can still upload.
    }
    this.hasFetchedSavedJst.set(true);
  }

  public closeJstSidebar(): void {
    // Force the next open to refetch: an upload in the ACE panel, or a confirm,
    // can change the list while this panel is closed.
    this.documentsFresh = false;
    this.isSidebarOpen$.set(false);
    this.closedSidebarSubscription?.unsubscribe();
    this.closedSidebarSubscription = null;
    if (this.overlayRef?.hasAttached()) {
      this.a11y?.release();
      this.a11y = null;
      this.overlayRef.detach();
    }
  }

  private createOverlay(): void {
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      panelClass: 'left-sidebar-panel',
      backdropClass: 'cdk-overlay-blur-backdrop',
      // Match ConnectSidebarService — top('0') only. The inner
      // .jst-upload-panel pins itself to height: 100vh so the panel is
      // always full-viewport regardless of pane container offsets.
      positionStrategy: this.overlay.position().global().left('0').top('0')
    });

    this.overlayRef
      .backdropClick()
      .subscribe({ next: () => this.closeJstSidebar(), error: noop });
  }
}
